import { emit, listen } from "@tauri-apps/api/event";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { getDesktopChatAuthScope, isCurrentDesktopChatAuthScope, type DesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import { isTauri } from "./is-tauri";
import type { SystemToast } from "@/stores/notification-store";

const EVENT = "denker://dismiss-system-toast";
const ADD_EVENT = "denker://add-system-toast";
interface Dismissal {
  id: string;
  revision: string | null;
  authScope: DesktopChatAuthScope | null;
}

interface Addition {
  toast: SystemToast;
  expectedRevision: string | null;
  authScope: DesktopChatAuthScope | null;
}

function hasCurrentScope(scope: DesktopChatAuthScope | null): boolean {
  return scope === null ? getDesktopChatAuthScope() === null : isCurrentDesktopChatAuthScope(scope);
}

export function forwardSystemToastAddition(toast: SystemToast, expectedRevision: string | null): boolean {
  if (!isTauri() || getWindowRoleSync() !== "mirror") return false;
  void emit(ADD_EVENT, { toast, expectedRevision, authScope: getDesktopChatAuthScope() }).catch(() => undefined);
  return true;
}

export function listenForSystemToastAdditions(add: (toast: SystemToast, expectedRevision: string | null) => void): void {
  if (!isTauri()) return;
  void listen<Addition>(ADD_EVENT, ({ payload }) => {
    if (getWindowRoleSync() !== "owner" || !hasCurrentScope(payload.authScope)) return;
    const { toast, expectedRevision } = payload;
    if (!toast || typeof toast.id !== "string" || typeof toast.revision !== "string" || typeof toast.title !== "string") return;
    if (payload.authScope === null && toast.appWide !== true) return;
    if (!["info", "warning", "error"].includes(toast.level)) return;
    if (expectedRevision !== null && typeof expectedRevision !== "string") return;
    add(toast, expectedRevision);
  }).catch(() => undefined);
}

export function forwardSystemToastDismissal(id: string, revision: string | null): boolean {
  if (!isTauri() || getWindowRoleSync() !== "mirror") return false;
  void emit(EVENT, { id, revision, authScope: getDesktopChatAuthScope() }).catch(() => undefined);
  return true;
}

/** Installed once per webview; role and auth checks also cover owner handoff. */
export function listenForSystemToastDismissals(remove: (id: string, revision: string | null) => void): void {
  if (!isTauri()) return;
  void listen<Dismissal>(EVENT, ({ payload }) => {
    if (getWindowRoleSync() !== "owner" || !hasCurrentScope(payload.authScope)) return;
    if (typeof payload.id !== "string" || (payload.revision !== null && typeof payload.revision !== "string")) return;
    remove(payload.id, payload.revision);
  }).catch(() => undefined);
}
